import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from 'store';

import { fetchCostCenter } from 'actions/costCenter';
import useSelectionOrigin from 'hooks/useSelectionOrigin';
import useToast from 'hooks/useToast';
import { CostCenter } from 'models/costCenter';
import CostCenterForm from 'screens/components/CostCenter';
import { saveCostCenter } from 'services/costCenterService';

interface Params {
  match: {
    params: {
      costCenterId: string;
    };
  };
  originRule?: string;
  isFromSelection?: boolean;
  onCloseFormCostCenter?: () => void;
}

const CostCenterFormContainer = ({ match: { params }, onCloseFormCostCenter, isFromSelection, originRule }: Params) => {
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(!!params.costCenterId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [costCenter, setCostCenter] = useState<CostCenter>();
  const fromOrigin = useSelectionOrigin();

  const { accountResult } = useSelector((state: RootState) => state.user);
  if (!accountResult) return null;
  const { companyId, countryCurrency } = accountResult;

  const loadCostCenter = useCallback(async () => {
    try {
      if (params.costCenterId && isLoading) {
        const {
          data: { data },
        } = await fetchCostCenter(params.costCenterId);

        setCostCenter(data);
      }
    } catch (error) {
      toast.add({ type: 'danger', message: 'Error' });
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, params.costCenterId, toast]);

  useEffect(() => {
    loadCostCenter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveCostCenter = async (costCenter: CostCenter) => {
    setIsSubmitting(true);
    try {
      const data = await saveCostCenter({
        ...costCenter,
        companyId,
      });

      setIsSubmitting(false);
      if (isFromSelection) {
        fromOrigin.updateItemCreated([data]);
        return;
      }
    } catch (e) {
      setIsSubmitting(false);
      throw e;
    }
  };

  return (
    <CostCenterForm
      isFromSelection={isFromSelection}
      loading={isLoading}
      submitting={isSubmitting}
      costCenter={costCenter}
      countryCurrency={countryCurrency}
      onConfirm={handleSaveCostCenter}
      onCloseFormCostCenter={onCloseFormCostCenter}
    />
  );
};

export default CostCenterFormContainer;
